import jsPDF from "jspdf";
import logo from "../assets/images/licomeds-logo-transparent.png";

interface DonationData {
  amount: number;
  donorName: string;
  donorEmail: string;
  isAnonymous: boolean;
  transactionId: string;
  date: string;
}

export const generateDonationReceipt = async (
  donationData: DonationData
): Promise<void> => {
  try {
    // Create new PDF document - A4 size
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    // Set white background
    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, 0, 210, 297, "F");

    // Professional header with gradient effect
    pdf.setFillColor(220, 38, 38);
    pdf.rect(0, 0, 210, 30, "F");

    // Add logo with white background circle for visibility
    let logoLoaded = false;
    try {
      const logoImg = new Image();
      logoImg.crossOrigin = "anonymous";

      await new Promise((resolve, reject) => {
        logoImg.onload = () => {
          logoLoaded = true;
          resolve(null);
        };
        logoImg.onerror = () => {
          console.warn("Logo not found");
          resolve(null);
        };
        logoImg.src = logo;
      });

      if (logoLoaded && logoImg.complete && logoImg.naturalHeight !== 0) {
        // White circle background for logo visibility
        pdf.setFillColor(255, 255, 255);
        pdf.circle(35, 15, 8, "F");

        // Logo positioned in white circle
        const logoWidth = 12;
        const logoHeight = 8;
        const logoX = 35 - logoWidth / 2;
        const logoY = 15 - logoHeight / 2;
        pdf.addImage(
          logoImg,
          "PNG",
          logoX,
          logoY,
          logoWidth,
          logoHeight,
          undefined,
          "FAST"
        );

        // Company name beside logo
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(18);
        pdf.setTextColor(255, 255, 255);
        pdf.text("LicoMeds", 50, 12);

        // Tagline
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(11);
        pdf.text("Making Healthcare Accessible", 50, 20);
      } else {
        // Fallback: company name centered
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(20);
        pdf.setTextColor(255, 255, 255);
        pdf.text("LicoMeds", 105, 12, { align: "center" });

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(12);
        pdf.text("Making Healthcare Accessible", 105, 20, { align: "center" });
      }
    } catch (error) {
      // Fallback: company name centered
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(20);
      pdf.setTextColor(255, 255, 255);
      pdf.text("LicoMeds", 105, 12, { align: "center" });

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(12);
      pdf.text("Making Healthcare Accessible", 105, 20, { align: "center" });
    }

    // Receipt info in top right
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(255, 255, 255);
    pdf.text(`Receipt #${donationData.transactionId.slice(-8)}`, 190, 12, {
      align: "right",
    });
    pdf.text(formatDate(donationData.date), 190, 19, { align: "right" });

    // Main title
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(28);
    pdf.setTextColor(31, 41, 55);
    pdf.text("DONATION RECEIPT", 105, 50, { align: "center" });

    // Decorative line
    pdf.setDrawColor(220, 38, 38);
    pdf.setLineWidth(2);
    pdf.line(50, 55, 160, 55);

    // Thank you message
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(13);
    pdf.setTextColor(75, 85, 99);
    pdf.text(
      "Thank you for your generous donation to healthcare accessibility!",
      105,
      70,
      { align: "center" }
    );

    let yPos = 90;

    // Full width Donation Details section
    const sectionWidth = 170;
    const sectionX = 20;

    // Donation Details - Full Width
    pdf.setFillColor(248, 250, 252);
    pdf.setDrawColor(226, 232, 240);
    pdf.setLineWidth(0.8);
    pdf.roundedRect(sectionX, yPos, sectionWidth, 120, 4, 4, "FD");

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(18);
    pdf.setTextColor(31, 41, 55);
    pdf.text("Donation Details", sectionX + 10, yPos + 15);

    let detailY = yPos + 30;

    // Show complete donation details without truncation
    const donationDetails = [
      ["Amount", formatCurrency(donationData.amount)],
      [
        "Donor",
        donationData.isAnonymous ? "Anonymous" : donationData.donorName,
      ],
      ["Email", donationData.donorEmail],
      ["Transaction ID", donationData.transactionId],
      ["Date", formatDateShort(donationData.date)],
      ["Payment Method", "Paystack (Secure Payment)"],
    ];

    // Create two columns within the full-width section
    const leftColX = sectionX + 10;
    const rightColX = sectionX + 90;
    let leftY = detailY;
    let rightY = detailY;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(11);

    donationDetails.forEach(([label, value], index) => {
      const currentX = index % 2 === 0 ? leftColX : rightColX;
      const currentY = index % 2 === 0 ? leftY : rightY;

      // Label in gray
      pdf.setTextColor(107, 114, 128);
      pdf.text(`${label}:`, currentX, currentY);

      // Value in black and bold
      pdf.setTextColor(31, 41, 55);
      pdf.setFont("helvetica", "bold");

      // Handle text wrapping for long values
      const maxWidth = 70;
      if (label === "Email" || label === "Transaction ID") {
        const wrappedText = pdf.splitTextToSize(value, maxWidth);
        pdf.text(wrappedText, currentX, currentY + 5);

        if (index % 2 === 0) {
          leftY += Math.max(wrappedText.length * 4 + 15, 20);
        } else {
          rightY += Math.max(wrappedText.length * 4 + 15, 20);
        }
      } else {
        pdf.text(value, currentX, currentY + 5);

        if (index % 2 === 0) {
          leftY += 20;
        } else {
          rightY += 20;
        }
      }

      pdf.setFont("helvetica", "normal");
    });

    // Thank you section - positioned higher to leave space for footer at bottom
    yPos = 240;
    pdf.setFillColor(34, 197, 94);
    pdf.rect(0, yPos, 210, 25, "F");

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(16);
    pdf.setTextColor(255, 255, 255);
    pdf.text("Thank You for Making a Difference!", 105, yPos + 10, {
      align: "center",
    });

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.text(
      "Your contribution directly supports healthcare accessibility across Africa",
      105,
      yPos + 18,
      { align: "center" }
    );

    // Footer - positioned at the very bottom of the page
    yPos = 275;
    pdf.setFillColor(31, 41, 55);
    pdf.rect(0, yPos, 210, 22, "F");

    // Company info
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(14);
    pdf.setTextColor(255, 255, 255);
    pdf.text("LicoMeds", 105, yPos + 8, { align: "center" });

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.text(
      "Email: support@licomeds.com  |  Website: www.licomeds.com",
      105,
      yPos + 15,
      { align: "center" }
    );

    // Legal notice in very small text
    pdf.setFontSize(7);
    pdf.setTextColor(156, 163, 175);
    pdf.text(
      `This receipt serves as proof of your donation. Generated on ${new Date().toLocaleDateString(
        "en-US"
      )}`,
      105,
      yPos + 20,
      { align: "center" }
    );

    // Generate filename
    const fileName = `LicoMeds_Receipt_${donationData.transactionId.slice(
      -8
    )}.pdf`;

    // Save the PDF
    pdf.save(fileName);
  } catch (error) {
    console.error("Error generating PDF receipt:", error);
    throw new Error("Failed to generate receipt. Please try again.");
  }
};

const formatCurrency = (amount: number): string => {
  // Use NGN format as shown in the example
  return `NGN ${amount.toLocaleString("en-NG")}`;
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDateShort = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};
